import {PercentageCircle} from '@github-ui/percentage-circle'
import {Box, Label, Text} from '@primer/react'
import {graphql, useQueryLoader, type PreloadedQuery, usePreloadedQuery, useFragment} from 'react-relay'

import {LABELS} from '../../constants/labels'
import {ChecklistIcon} from '@primer/octicons-react'
import {useEffect} from 'react'
import type {TaskListStatusQuery} from './__generated__/TaskListStatusQuery.graphql'
import type {TaskListStatusFragment$key} from './__generated__/TaskListStatusFragment.graphql'

type TaskListStatus = {
  taskListSummary: {
    itemCount: number
    completeCount: number
  }
  tasklistBlocksCompletion: {
    completed: number
    total: number
  }
}

type TaskListStatusProps = {
  issueId: string
}

type TaskListStatusFetchedProps = {
  taskListStatusKey: TaskListStatusFragment$key
}

type TaskListStatusInternalProps = {
  queryRef: PreloadedQuery<TaskListStatusQuery>
}

export const TaskListStatusFragment = graphql`
  fragment TaskListStatusFragment on Issue {
    taskListSummary {
      itemCount
      completeCount
    }
    tasklistBlocksCompletion {
      completed
      total
    }
  }
`

export const TaskListStatusGraphQLQuery = graphql`
  query TaskListStatusQuery($issueId: ID!) {
    node(id: $issueId) {
      ... on Issue {
        ...TaskListStatusFragment
      }
    }
  }
`

export function TaskListStatus({issueId}: TaskListStatusProps) {
  const [queryRef, loadQueryRef] = useQueryLoader<TaskListStatusQuery>(TaskListStatusGraphQLQuery)

  useEffect(() => {
    loadQueryRef({issueId}, {fetchPolicy: 'store-only'})
  }, [loadQueryRef, issueId])

  if (!queryRef) {
    return null
  }

  return <TaskListStatusInternal queryRef={queryRef} />
}

export function TaskListStatusInternal({queryRef}: TaskListStatusInternalProps) {
  const data = usePreloadedQuery<TaskListStatusQuery>(TaskListStatusGraphQLQuery, queryRef)
  if (!data.node) {
    return null
  }

  return <TaskListStatusFetched taskListStatusKey={data.node} />
}

function TaskListStatusFetched({taskListStatusKey}: TaskListStatusFetchedProps) {
  const {tasklistBlocksCompletion, taskListSummary} = useFragment(TaskListStatusFragment, taskListStatusKey)
  if (!tasklistBlocksCompletion && !taskListSummary) return null

  // If tasklist block is deleted but there's still a checklist, we need to check if
  // the tasklist block total is zero, in order to show the progress bar for checklist instead
  const hasTaskListBlock = tasklistBlocksCompletion && tasklistBlocksCompletion.total > 0

  let total
  let completed

  if (hasTaskListBlock) {
    total = tasklistBlocksCompletion.total
    completed = tasklistBlocksCompletion.completed
  } else if (taskListSummary) {
    total = taskListSummary.itemCount
    completed = taskListSummary.completeCount
  } else {
    return null
  }

  const progress = completed / total
  if (total === 0 || Object.is(NaN, progress)) return null

  return (
    <>
      <Box
        sx={{
          whiteSpace: 'nowrap',
          display: 'inline-flex',
          alignItems: 'center',
          gap: 1,
          verticalAlign: 'middle',
        }}
      >
        <Box data-testid="tasklist-progress" as="span" sx={{display: 'inline-flex', alignItems: 'center', gap: 1}}>
          <Label sx={{color: 'fg.muted', px: 'var(--control-small-paddingInline-normal)', gap: 1, height: '32px'}}>
            {!hasTaskListBlock && completed === 0 ? (
              <ChecklistIcon size={16} />
            ) : (
              <PercentageCircle progress={progress} radius={7} isSuccess />
            )}

            <Text sx={{fontSize: 0, fontWeight: 'normal'}}>{LABELS.tasks(completed, total)}</Text>
          </Label>
        </Box>
      </Box>
    </>
  )
}

try{ TaskListStatus.displayName ||= 'TaskListStatus' } catch {}
try{ TaskListStatusInternal.displayName ||= 'TaskListStatusInternal' } catch {}
try{ TaskListStatusFetched.displayName ||= 'TaskListStatusFetched' } catch {}